import { ___p, _js } from '@ifixit/utils/localize';
import { Alert, AlertDescription, AlertTitle } from '@ifixit/ui/components/alert';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { toast } from '@ifixit/ui/components/toast';
import { cn } from '@ifixit/ui/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
   memo,
   type Ref,
   useCallback,
   useEffect,
   useId,
   useImperativeHandle,
   useMemo,
   useRef,
   useState,
} from 'react';
import { BAR_CLASSES, WriteStatusAnnouncer } from 'Shared/editor/action_bar';
import { apiErrorMessage } from 'Shared/editor/api_error_message';
import { DangerConfirmButton, PrimaryButton } from 'Shared/editor/buttons';
import { LineCount } from 'Shared/editor/line_count';
import { useReorderAnnouncement } from 'Shared/editor/use_reorder_announcement';
import { useSaveShortcut } from 'Shared/editor/use_save_shortcut';
import { BulletSelector } from './BulletSelector';
import { cycledBullet } from './bullets';
import { LineButton } from 'Shared/editor/IconActionButton';
import { type LineShortcutEvent, LineWysiwyg } from './LineWysiwyg';
import {
   canAddLine,
   canDedent,
   canDelete,
   canIndent,
   canMoveDown,
   canMoveUp,
   dedentLine,
   deleteLine,
   type EditorLine,
   indentLine,
   insertLineAbove,
   insertLineBelow,
   isBlankLine,
   lineDisplayText,
   type LineProblem,
   MAX_LINE_CHARS,
   moveLineDown,
   moveLineUp,
   restoreLine,
   validateLines,
   visibleLength,
} from './line_rules';
import { buildStepPayload } from './step_payload';
import { linesFromStep, type StepSeed } from './step_seed';
import { applyStepTimestamps, parseStepTimestamps, type StepTimestamps } from './timestamp_tokens';
import { mediaSelectionFromApi, type MediaSelection, StepMediaSelector } from './StepMediaSelector';
import { StepTimestampEditor } from './StepTimestampEditor';
import type { ApiGuide, ApiStep, GuideVideoInfo } from './types';
import {
   lineDocQueryOptions,
   StepConflictError,
   useDeleteStep,
   useRenderLine,
   useSaveStep,
} from './use_guide_edit_api';
import { type LineDragHandleProps, type LineDragRowProps, useLineDrag } from './use_line_drag';
import { UNDO_TOAST_DURATION_MS } from 'Shared/editor/undo_toast';

/**
 * The timestamp fields as StepTimestamps: null when both are blank (no
 * token), 'invalid' when half-filled, unparseable, or start >= end.
 * Negatives clamp to 0.
 */
function fieldTimestamps(fields: {
   start: string;
   end: string;
}): StepTimestamps | null | 'invalid' {
   const startText = fields.start.trim();
   const endText = fields.end.trim();
   if (startText === '' && endText === '') {
      return null;
   }
   if (startText === '' || endText === '') {
      return 'invalid';
   }
   const start = Math.max(0, Number.parseFloat(startText));
   const end = Math.max(0, Number.parseFloat(endText));
   if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
      return 'invalid';
   }
   return { start, end };
}

/**
 * The content payload a pristine editor for this step would produce.
 * orderby is excluded on purpose — it's a create-time position, not part of
 * the content, so it never counts toward dirtiness.
 */
function stepBaselinePayload(step: ApiStep | null) {
   return buildStepPayload({
      title: step?.title ?? '',
      lines: linesFromStep(step),
      media:
         step?.media && step.media.type !== 'image'
            ? { type: 'object', objectid: step.media.data?.objectid ?? null }
            : {
                 type: 'image',
                 imageids:
                    step?.media?.type === 'image' ? step.media.data.map(image => image.id) : [],
              },
   });
}

function problemMessage(problem: LineProblem): string {
   switch (problem.code) {
      case 'too-many-lines': {
         return _js('Steps can only have eight lines so they look great in all formats.');
      }
      case 'line-too-long': {
         return _js('Line %1 is over %2 characters.', String(problem.line), String(MAX_LINE_CHARS));
      }
   }
}

/**
 * Editor for one step: title, the line tree, and the media selector. Saves
 * through the per-step API endpoint with revisionid concurrency; on a 409
 * the server's current step is offered instead of silently overwriting.
 */
/**
 * Lets the step switcher wait for this step to be saved. onSaved runs once
 * the save lands (after onCreated, for a new step), never for a failed one.
 */
export type SaveStepHandle = {
   /**
    * Starts a save, or waits on the one in flight; with nothing to save,
    * calls onSaved at once. False when onSaved won't run: validation
    * stopped the save, or a delete is in flight.
    */
   save: (onSaved: () => void) => boolean;
   /** Waits on the save in flight; false when there is none. */
   afterPendingSave: (onSaved: () => void) => boolean;
};

/** A step beside the open one, as the footer pager names it. */
export type NeighborStep = {
   stepid: number;
   /** The number its rail row shows, so a draft row above it counts. */
   number: number;
   title: string;
};

export function StepEditor({
   guideid,
   step,
   seed,
   orderby,
   video,
   canEditTitle,
   onDirtyChange,
   onCreated,
   onDeleted,
   saveRef,
   previousStep,
   nextStep,
   onSelectStep,
}: {
   guideid: number;
   /** null edits a brand-new unsaved step. */
   step: ApiStep | null;
   /**
    * Content typed while a create POST was in flight, carried over so it
    * isn't lost when the editor remounts onto the created step. Overrides
    * `step` for initial state; `step` still supplies the clean baseline
    * (the server holds the submitted content, so the editor lands dirty by
    * exactly the carried-over edits).
    */
   seed?: StepSeed;
   orderby: number;
   /** The guide's video; null on non-video guides (no timestamp editor). */
   video: GuideVideoInfo | null;
   /** False for students: an existing title shows read-only, and none can be added. */
   canEditTitle: boolean;
   onDirtyChange: (dirty: boolean) => void;
   onCreated: (stepid: number, seed?: StepSeed) => void;
   onDeleted: () => void;
   /** Lets the step switcher save this step, or wait on its save, before switching away. */
   saveRef?: Ref<SaveStepHandle>;
   /** The steps either side of this one; null at an end. No pager without onSelectStep. */
   previousStep?: NeighborStep | null;
   nextStep?: NeighborStep | null;
   /**
    * Goes to a neighbor through the step switcher, which guards unsaved
    * changes. The direction lets the switcher hand focus to the pager
    * button of the editor that replaces this one.
    */
   onSelectStep?: (stepid: number, direction: 'previous' | 'next') => void;
}) {
   // Stable per-instance id for the bare title field, which has an aria-label
   // but would otherwise render with no id at all.
   const fieldBaseId = useId();
   const [title, setTitle] = useState(seed?.title ?? step?.title ?? '');
   // Most steps have no title, so the input hides behind a button until one
   // exists (or the user asks for it). Never auto-hides: clearing the text
   // mid-edit must not collapse the field under the cursor.
   const [titleVisible, setTitleVisible] = useState(
      () => (seed?.title ?? step?.title ?? '') !== ''
   );
   // Set only by the button click, so autoFocus doesn't steal focus on a
   // step that loads with its title already visible.
   const [titleAutoFocus, setTitleAutoFocus] = useState(false);
   const [lines, setLines] = useState<EditorLine[]>(() => seed?.lines ?? linesFromStep(step));
   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);
   const [media, setMedia] = useState<MediaSelection>(
      () => seed?.media ?? mediaSelectionFromApi(step?.media)
   );
   const [revisionid, setRevisionid] = useState(step?.revisionid ?? '');
   const [conflict, setConflict] = useState<ApiStep | null>(null);
   // The line being edited and its in-progress text (raw wikitext, as the
   // WYSIWYG serializes it). Owning the draft here lets the payload,
   // validation, and Save all see keystrokes before the edit commits.
   const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
   // Armed by a failed save; from then on problems track the lines live,
   // so fixing a line clears its error as you type.
   const [validationArmed, setValidationArmed] = useState(false);
   // Ctrl+Up/Down line reorders are silent otherwise — a sighted user sees
   // the row jump, but nothing tells a screen reader it happened.
   const { announcement: reorderAnnouncement, announce: announceReorder } =
      useReorderAnnouncement();

   const videoType = video?.type ?? 'none';
   const tokenType = videoType === 'youtube' ? 'youtubetimestamp' : 'videotimestamp';
   // The token in the first line is the storage; the fields are a friendlier
   // handle on it. Seeded from the stored lines, tracked as text so partial
   // input doesn't jump around while typing.
   const [timestampBaseline, setTimestampBaseline] = useState(
      () =>
         seed?.timestampBaseline ??
         parseStepTimestamps(step?.lines?.map(line => ({ text: line.text_raw })) ?? [])
   );
   const [timestampFields, setTimestampFields] = useState(() => {
      if (seed) {
         return seed.timestampFields;
      }
      return {
         start: timestampBaseline === null ? '' : String(timestampBaseline.start),
         end: timestampBaseline === null ? '' : String(timestampBaseline.end),
      };
   });

   const stepHasMedia = media.type === 'image' ? media.images.length > 0 : media.objectid !== null;

   const saveStep = useSaveStep(guideid);
   const deleteStep = useDeleteStep(guideid);
   // Mutation pending state updates on render. The ref closes the gap so a
   // second action cannot submit the same revision token first.
   const writePendingRef = useRef<'save' | 'delete' | null>(null);
   // Whoever asked last to hear that the save in flight landed: the prompt's
   // Save and switch, or a step switch made while it ran.
   const afterSaveRef = useRef<(() => void) | null>(null);
   const renderLine = useRenderLine();
   const queryClient = useQueryClient();
   // Drag a line (with its subtree) to reorder or, by moving sideways,
   // re-indent it; the drop commits through the same rules as the buttons.
   const drag = useLineDrag(lines, setLines);
   const viewLines = drag.previewLines ?? lines;

   // Warm the per-line doc cache so opening a line for the first time
   // doesn't wait on the server parse. Later texts are covered by the
   // editor seeding the cache with its own doc as it closes.
   useEffect(() => {
      for (const line of lines) {
         if (line.text.trim() !== '') {
            void queryClient.prefetchQuery(lineDocQueryOptions(line.text));
         }
      }
      // Mount-only: edited texts get cache-seeded by the closing editor,
      // so re-running per lines change would mostly re-request cache hits.
      // A miss (e.g. a post-save reseed the server normalized) just fetches
      // on demand when the line is opened.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Lines with the in-flight draft applied.
   const effectiveLines = useMemo(
      () =>
         editing === null
            ? lines
            : lines.map(line =>
                 line.key === editing.key ? { ...line, text: editing.text } : line
              ),
      [lines, editing]
   );

   // The lines exactly as the save will send them: the token rewrite is
   // applied here so validation and the request body agree (validating the
   // pre-rewrite lines let a token-only line pass the client and 422 on the
   // server, and blocked a line the rewrite would have filled).
   const payloadLines = useMemo(
      () => {
         const edited = fieldTimestamps(timestampFields);
         // Only rewrite the token when the fields actually moved off the
         // stored values: a pristine step round-trips its raw text
         // untouched, hand-edited tokens included. Invalid half-filled
         // fields never rewrite; save blocks on them instead.
         const rewriteToken =
            videoType !== 'none' &&
            edited !== 'invalid' &&
            JSON.stringify(edited) !== JSON.stringify(timestampBaseline);
         return rewriteToken
            ? applyStepTimestamps(effectiveLines, edited as StepTimestamps | null, tokenType)
            : effectiveLines;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [effectiveLines, timestampFields, timestampBaseline]
   );

   // Memoized: the forceMount tabs re-render this component on every
   // keystroke anywhere on the page, so rebuilding and stringifying the
   // payload each time was wasted work on the hottest path. orderby is not
   // part of the content payload — it's a create-time position applied at
   // POST time, so a refetch that changes it can't stale this memo.
   const payload = useMemo(
      () =>
         buildStepPayload({
            title,
            lines: payloadLines,
            media:
               media.type === 'image'
                  ? { type: 'image', imageids: media.images.map(image => image.imageid) }
                  : { type: 'object', objectid: media.objectid },
         }),
      [title, payloadLines, media]
   );
   const payloadJson = useMemo(() => JSON.stringify(payload), [payload]);
   // The save-success callback otherwise closes over the submission-time
   // render. Keep the latest payload and its corresponding rich editor state
   // together so a create remount can carry every in-flight edit forward.
   const latestStateRef = useRef({
      payloadJson,
      seed: {
         title,
         lines: effectiveLines,
         media,
         timestampBaseline,
         timestampFields,
      } satisfies StepSeed,
   });
   useEffect(() => {
      latestStateRef.current = {
         payloadJson,
         seed: {
            title,
            lines: effectiveLines,
            media,
            timestampBaseline,
            timestampFields,
         },
      };
   }, [payloadJson, title, effectiveLines, media, timestampBaseline, timestampFields]);
   // Live, not save-time: a half-filled pair keeps the payload pristine
   // (nothing valid to write), which disables Save — so a save-time message
   // could never appear to explain why.
   const timestampsInvalid = videoType !== 'none' && fieldTimestamps(timestampFields) === 'invalid';

   // The dirty baseline moves on every reseed (post-save, load-theirs), not
   // just on mount — the editor is keyed by stepid, so a save no longer
   // remounts it.
   const [baselineJson, setBaselineJson] = useState(() =>
      JSON.stringify(stepBaselinePayload(step))
   );

   // Lines get fresh client keys on every seed, so compare payloads (which
   // exclude keys and rendered previews), not the state objects. An
   // untouched new-step form compares equal to its empty seed, so it doesn't
   // arm the unsaved-changes prompts.
   const isDirty = payloadJson !== baselineJson;
   useEffect(() => {
      onDirtyChange(isDirty);
   });

   const saveDisabled = saveStep.isPending || deleteStep.isPending || !isDirty;

   const updateLine = useCallback((key: string, update: Partial<EditorLine>) => {
      setLines(prev => prev.map(line => (line.key === key ? { ...line, ...update } : line)));
   }, []);

   const commitLine = useCallback(
      (line: EditorLine, text: string) => {
         if (text === line.text && line.rendered !== null) {
            return;
         }
         updateLine(line.key, { text, rendered: null });
         if (text.trim().length === 0) {
            return;
         }
         // mutateAsync, not mutate: a shared mutation observer only fires its
         // per-call onSuccess for the latest mutate(), so committing a second
         // line before the first render returned would drop the first line's
         // rendered HTML. The awaited promise settles per call regardless.
         void renderLine.mutateAsync(text).then(
            html =>
               setLines(prev =>
                  prev.map(candidate =>
                     candidate.key === line.key && candidate.text === text
                        ? { ...candidate, rendered: html }
                        : candidate
                  )
               ),
            () => {
               // Render failed; the line keeps showing its raw text.
            }
         );
      },
      [renderLine, updateLine]
   );

   // The in-flight draft, maintained at event time alongside the `editing`
   // state. Two reasons the state alone doesn't serve: a line editor that
   // just handed editing over (Tab, Enter, Ctrl+N/P) unmounts, and its blur
   // then fires through closures frozen at its last render — where `editing`
   // still pointed at itself; and reading the draft here instead of from
   // state keeps the editing callbacks below identity-stable across
   // keystrokes, so LineRow's memo actually holds while a line is typed.
   const editingRef = useRef<{ key: string; text: string } | null>(null);
   const changeEditing = useCallback((next: { key: string; text: string } | null) => {
      editingRef.current = next;
      setEditing(next);
   }, []);

   const onLineDraftChange = useCallback((text: string) => {
      editingRef.current = editingRef.current && { ...editingRef.current, text };
      setEditing(current => current && { ...current, text });
   }, []);

   const finishEditing = useCallback(() => {
      const current = editingRef.current;
      if (current === null) {
         return;
      }
      const line = lines.find(candidate => candidate.key === current.key);
      if (line) {
         commitLine(line, current.text);
      }
      changeEditing(null);
   }, [lines, commitLine, changeEditing]);

   /**
    * Finish, but only if `key` is still the line being edited: blurs from
    * lines that already handed editing over are ignored.
    */
   const finishEditingFor = useCallback(
      (key: string) => {
         if (editingRef.current?.key !== key) {
            return;
         }
         finishEditing();
      },
      [finishEditing]
   );

   /** Commit the current draft and move editing to another line. */
   const beginEditingAt = useCallback(
      (targetIndex: number) => {
         const target = lines[targetIndex];
         if (!target) {
            return;
         }
         finishEditing();
         changeEditing({ key: target.key, text: target.text });
      },
      [lines, finishEditing, changeEditing]
   );

   /** Insert a fresh line next to `index` and move editing into it. */
   const insertAndEdit = useCallback(
      (index: number, where: 'above' | 'below') => {
         const inserted =
            where === 'below' ? insertLineBelow(lines, index) : insertLineAbove(lines, index);
         if (inserted.length === lines.length) {
            // At the line cap; the rules returned the list unchanged.
            return;
         }
         setLines(inserted);
         // Commit the in-flight draft before the editor moves (updateLine
         // merges by key, so it lands on the inserted list).
         const draft = editingRef.current;
         if (draft !== null) {
            const current = lines.find(candidate => candidate.key === draft.key);
            if (current) {
               commitLine(current, draft.text);
            }
         }
         const added = inserted[where === 'below' ? index + 1 : index];
         changeEditing({ key: added.key, text: '' });
      },
      [lines, commitLine, changeEditing]
   );

   // Lines deleted while the Undo toast is up, oldest first. Each delete
   // re-raises the same toast id, so one toast and one Undo cover them all.
   const deletedLinesRef = useRef<{ removed: EditorLine; beforeDelete: EditorLine[] }[]>([]);

   /** Delete the line at `index` and move editing to its successor. */
   const deleteLineAt = useCallback(
      (index: number) => {
         if (!canDelete(lines, index)) {
            return;
         }
         const removed = lines[index];
         const remaining = deleteLine(lines, index);
         setLines(remaining);
         const next = remaining[Math.min(index, remaining.length - 1)];
         changeEditing(next ? { key: next.key, text: next.text } : null);
         deletedLinesRef.current = [...deletedLinesRef.current, { removed, beforeDelete: lines }];
         const count = deletedLinesRef.current.length;
         const forgetDeletedLines = () => {
            deletedLinesRef.current = [];
         };
         toast.custom({
            id: 'line-deleted',
            title: ___p(count, 'Line deleted.', '%1 lines deleted.', String(count)),
            duration: UNDO_TOAST_DURATION_MS,
            onAutoClose: forgetDeletedLines,
            onDismiss: forgetDeletedLines,
            action: {
               label: _js('Undo'),
               onClick: () => {
                  // Read now: the toast calls onDismiss before React runs the
                  // setLines updater below.
                  const deleted = deletedLinesRef.current;
                  // A step switch remounts the editor while the toast stays up,
                  // and React drops a state update on the dead instance.
                  if (!aliveRef.current) {
                     toast.error(
                        ___p(
                           deleted.length,
                           'The step editor closed, so the line was not restored.',
                           'The step editor closed, so the lines were not restored.'
                        ),
                        { duration: 5000 }
                     );
                     return;
                  }
                  // Newest first, the reverse of the deletes, so each line's
                  // snapshot finds the neighbors it was deleted from.
                  setLines(current =>
                     deleted.reduceRight(
                        (restored, { removed, beforeDelete }) =>
                           restoreLine(restored, removed, beforeDelete),
                        current
                     )
                  );
               },
            },
         });
      },
      [lines, changeEditing]
   );

   /**
    * Applies a Ctrl+Up/Down reorder and tells a screen reader where the line
    * landed — the row jump is otherwise silent to AT, unlike a pointer drag
    * which at least gives sighted feedback. Position is 1-based over the
    * full line list (not just siblings), matching how useDragReorder
    * announces a step or filmstrip-tile move.
    */
   const announceLineMove = useCallback(
      (movedKey: string, next: EditorLine[]) => {
         setLines(next);
         const position = next.findIndex(line => line.key === movedKey);
         if (position !== -1) {
            announceReorder(position + 1, next.length);
         }
      },
      [setLines, announceReorder]
   );

   /**
    * The line-editing shortcuts, active while a line's editor has
    * focus. True when the event was one of them (and consumed).
    */
   const handleLineShortcut = useCallback(
      (index: number, event: LineShortcutEvent): boolean => {
         // Enter commits the line and opens the next for editing, adding a
         // fresh line when there's none below — so a whole step types out line
         // by line without the mouse. Shift+Enter inserts a line above instead.
         // Ctrl+Shift+Enter skips this block unconsumed, for the page-level
         // add-a-step handler. It only reaches that handler from outside a
         // line: tiptap's keymap claims the chord and preventDefaults before
         // it leaves the editable, and the page handler skips a key another
         // handler already took. Verified in the browser, not in the specs,
         // which mock the line editor as a plain textarea.
         if (event.key === 'Enter' && !event.ctrlKey && !event.altKey && !event.metaKey) {
            // From a blank line, Enter does nothing — no stacking a second
            // empty line off an already-empty one. The draft comes from the
            // ref, not effectiveLines, so this callback's identity survives
            // keystrokes and LineRow's memo holds.
            const line = lines[index];
            const draft = editingRef.current;
            const withDraft =
               draft !== null && line !== undefined && draft.key === line.key
                  ? { ...line, text: draft.text }
                  : line;
            if (isBlankLine(withDraft)) {
               event.preventDefault();
               return true;
            }
            if (event.shiftKey) {
               insertAndEdit(index, 'above');
            } else if (index < lines.length - 1) {
               beginEditingAt(index + 1);
            } else {
               insertAndEdit(index, 'below');
            }
            event.preventDefault();
            return true;
         }
         // Tab indents the current line, Shift+Tab unindents — matching the
         // list-editor convention editors reach for. The key is always trapped
         // (even at the indent limits) so it never tabs focus out of the open
         // editor, which is deliberately a focus trap; Escape or a click
         // elsewhere leaves it.
         if (event.key === 'Tab' && !event.ctrlKey && !event.altKey && !event.metaKey) {
            if (event.shiftKey) {
               if (canDedent(lines, index)) {
                  setLines(dedentLine(lines, index));
               }
            } else if (canIndent(lines, index)) {
               setLines(indentLine(lines, index));
            }
            event.preventDefault();
            return true;
         }
         if (!event.ctrlKey || event.metaKey) {
            return false;
         }
         const key = event.key.toLowerCase();
         // Letters go by physical position: event.key shifts with the layout
         // while event.code stays KeyN. But Windows reports AltGr as
         // Ctrl+Alt, so on a layout where AltGr+N composes a character (ñ on
         // US-International, ń on Polish) this chord is someone typing — the
         // Alt tier stands down whenever the chord composed a character, and
         // Ctrl+Enter / Shift+Enter stay the inserts that reach every layout.
         const letter = event.code;
         // Chrome keeps Ctrl+N (new window) and Ctrl+Shift+N (incognito) for
         // itself on Windows and Linux — a page never sees either — so the
         // insert pair lives on the Alt tier, which reaches every platform.
         // Ctrl+Enter is a second way to insert below, for hands already on
         // Enter from typing a step out line by line.
         if (key === 'enter' && !event.shiftKey && !event.altKey) {
            insertAndEdit(index, 'below');
            event.preventDefault();
            return true;
         }
         if (event.altKey) {
            if (key.length === 1 && key !== 'n' && key !== 'p') {
               return false;
            }
            if (letter === 'KeyN') {
               insertAndEdit(index, 'below');
            } else if (letter === 'KeyP') {
               insertAndEdit(index, 'above');
            } else {
               return false;
            }
            event.preventDefault();
            return true;
         }
         if (event.shiftKey) {
            return false;
         }
         if (letter === 'KeyP') {
            beginEditingAt(index - 1);
            event.preventDefault();
            return true;
         }
         if (letter === 'KeyD') {
            deleteLineAt(index);
            event.preventDefault();
            return true;
         }
         switch (key) {
            case ']': {
               if (canIndent(lines, index)) {
                  setLines(indentLine(lines, index));
               }
               break;
            }
            case '[': {
               if (canDedent(lines, index)) {
                  setLines(dedentLine(lines, index));
               }
               break;
            }
            case 'arrowup': {
               if (canMoveUp(lines, index)) {
                  announceLineMove(lines[index].key, moveLineUp(lines, index));
               }
               break;
            }
            case 'arrowdown': {
               if (canMoveDown(lines, index)) {
                  announceLineMove(lines[index].key, moveLineDown(lines, index));
               }
               break;
            }
            // Ctrl+. colors the line without leaving the keyboard: one press
            // per line walks ROYGBV down the step (the ramp continues from
            // the line above). Ctrl+, backs up an over-press.
            case '.': {
               updateLine(lines[index].key, { bullet: cycledBullet(lines, index, 1) });
               break;
            }
            case ',': {
               updateLine(lines[index].key, { bullet: cycledBullet(lines, index, -1) });
               break;
            }
            default: {
               return false;
            }
         }
         event.preventDefault();
         return true;
      },
      [lines, insertAndEdit, beginEditingAt, announceLineMove, deleteLineAt, updateLine]
   );

   const reseedFromStep = (saved: ApiStep) => {
      setTitle(saved.title ?? '');
      setTitleVisible(visible => visible || (saved.title ?? '') !== '');
      setLines(linesFromStep(saved));
      setMedia(mediaSelectionFromApi(saved.media));
      setRevisionid(saved.revisionid);
      setBaselineJson(JSON.stringify(stepBaselinePayload(saved)));
      const parsed = parseStepTimestamps(saved.lines.map(line => ({ text: line.text_raw })));
      setTimestampBaseline(parsed);
      setTimestampFields({
         start: parsed === null ? '' : String(parsed.start),
         end: parsed === null ? '' : String(parsed.end),
      });
      setConflict(null);
      changeEditing(null);
      setValidationArmed(false);
   };

   // The 409 body is the server's current step. Adopting its revisionid
   // means the next save or delete applies deliberately instead of failing
   // against the same stale token forever.
   const adoptConflict = (error: unknown) => {
      if (!(error instanceof StepConflictError)) {
         return;
      }

      setConflict(error.serverStep);
      setRevisionid(error.serverStep.revisionid);
   };

   const handleSave = (onSaved?: () => void) => {
      if (writePendingRef.current !== null || !isDirty) {
         return false;
      }
      if (validateLines(payloadLines).length > 0 || timestampsInvalid) {
         setValidationArmed(true);
         return false;
      }
      writePendingRef.current = 'save';
      afterSaveRef.current = onSaved ?? null;
      // orderby is read fresh here, not from the memo, so a refetch that
      // moved the insert position (another session added/removed a step)
      // POSTs the current position instead of the one captured at mount.
      // payloadJson is this closure's snapshot; the ref holds whatever it is
      // by the time the response lands.
      saveStep.mutate(
         {
            stepid: step?.stepid ?? null,
            revisionid,
            payload: step === null ? { ...payload, orderby } : payload,
         },
         {
            onSuccess: data => {
               // Steps save individually (no guide-level Save bar on this
               // tab), so this is the only "it saved" signal the author gets.
               toast.success(_js('Step saved.'), { duration: 4000 });
               if (step === null) {
                  // POST returns the whole guide; the new step sits at the
                  // orderby we asked for.
                  const created = (data as ApiGuide).steps[orderby - 1];
                  if (!created) {
                     return;
                  }
                  // Keystrokes typed while the POST was in flight would be
                  // lost when onCreated remounts this editor onto the new
                  // stepid; carry the newer live state over (rich, so media
                  // thumbnails survive) as the created step's starting
                  // content instead of discarding it.
                  onCreated(
                     created.stepid,
                     latestStateRef.current.payloadJson === payloadJson
                        ? undefined
                        : latestStateRef.current.seed
                  );
                  afterSaveRef.current?.();
                  return;
               }
               const saved = data as ApiStep;
               if (latestStateRef.current.payloadJson === payloadJson) {
                  reseedFromStep(saved);
                  afterSaveRef.current?.();
                  return;
               }
               // The user kept editing while the save ran. Reseeding would
               // clobber those newer edits, so just adopt the server's new
               // revisionid and move the clean baseline to what was saved —
               // the step stays dirty and saveable.
               setRevisionid(saved.revisionid);
               setBaselineJson(payloadJson);
               setConflict(null);
               afterSaveRef.current?.();
            },
            onError: adoptConflict,
            onSettled: () => {
               writePendingRef.current = null;
               afterSaveRef.current = null;
            },
         }
      );
      return true;
   };
   const afterPendingSave = (onSaved: () => void) => {
      if (writePendingRef.current !== 'save') {
         return false;
      }
      afterSaveRef.current = onSaved;
      return true;
   };
   useImperativeHandle(saveRef, () => ({
      save: onSaved => {
         if (afterPendingSave(onSaved)) {
            return true;
         }
         if (writePendingRef.current === null && !isDirty) {
            onSaved();
            return true;
         }
         return handleSave(onSaved);
      },
      afterPendingSave,
   }));

   // The tab panels are forceMounted, so this editor keeps its listener while
   // another tab is on screen — unguarded, Ctrl+S there would silently save
   // the off-screen step and race the guide-level save for the revision.
   // Hidden means display: none, so offsetParent is null (the same gate
   // StepsTab's document-level shortcuts use).
   const rootRef = useRef<HTMLDivElement>(null);
   useSaveShortcut(() => {
      if (!rootRef.current || rootRef.current.offsetParent === null) {
         return;
      }
      if (!saveDisabled) {
         handleSave();
      }
   });

   const handleDelete = () => {
      if (step === null) {
         onDeleted();
         return;
      }
      if (writePendingRef.current !== null) {
         return;
      }
      writePendingRef.current = 'delete';
      deleteStep.mutate(
         { stepid: step.stepid, revisionid },
         {
            onSuccess: () => onDeleted(),
            onError: adoptConflict,
            onSettled: () => {
               writePendingRef.current = null;
            },
         }
      );
   };

   const saveError = saveStep.error ?? deleteStep.error;
   // The conflict banner covers StepConflictErrors; any other failure (a
   // 422's validation message, say) must stay visible even while a
   // conflict is being resolved.
   const isConflictError = saveError instanceof StepConflictError;
   const problems: LineProblem[] = validationArmed ? validateLines(effectiveLines) : [];

   return (
      // h-full: the card stretches to the rail-sized row, so filling it
      // lets mt-auto put the action bar at the bottom of a short step.
      <div ref={rootRef} className="flex h-full flex-col gap-3">
         <WriteStatusAnnouncer isSaving={saveStep.isPending} isDeleting={deleteStep.isPending} />
         {canEditTitle ? (
            titleVisible ? (
               // No separate label line — the placeholder carries it, keeping
               // the row a single input tall so the image gets the height.
               <Input
                  id={`${fieldBaseId}-title`}
                  aria-label={_js('Step title (optional)')}
                  placeholder={_js('Step title (optional)')}
                  // Reads as the heading it will be on the guide page: no
                  // box until the pointer or focus is on it. -mx-2 + px-2
                  // keeps the box reaching past the text without moving the
                  // text off the content edge, and the Input's md
                  // pointer-fine size has to be overridden as well.
                  className="-mx-2 w-[calc(100%+1rem)] border-transparent bg-transparent px-2 text-xl/7 font-semibold text-gray-950 placeholder:font-normal hover:border-input md:pointer-fine:text-xl/7"
                  value={title}
                  // The v1 editor's limit. Translations run longer than the
                  // English and share its 255-char column, so they need room.
                  maxLength={125}
                  autoFocus={titleAutoFocus}
                  onChange={event => setTitle(event.target.value)}
                  // Enter drops straight into the first line, so a title and
                  // its steps can be typed in one keyboard run.
                  onKeyDown={event => {
                     if (event.key !== 'Enter' || event.shiftKey) {
                        return;
                     }

                     event.preventDefault();
                     beginEditingAt(0);
                  }}
               />
            ) : (
               // Titles are optional and on their way to becoming section
               // titles, so the affordance stays quiet: the same low-key
               // inline button as "Add a line", always visible (mobile too).
               // Clicking swaps in the input exactly where the title will
               // live (both rows are h-9, so nothing shifts).
               <button
                  type="button"
                  onClick={() => {
                     setTitleAutoFocus(true);
                     setTitleVisible(true);
                  }}
                  className="flex h-9 w-fit items-center gap-2 rounded-lg px-2 text-sm text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-[3px] focus-visible:ring-ring/50"
               >
                  <FaIcon name="plus" className="size-3.5" aria-hidden />
                  {_js('Add a title')}
               </button>
            )
         ) : (
            title !== '' && (
               <p className="m-0 flex min-h-9 items-center text-xl/7 font-semibold text-gray-950">
                  {title}
               </p>
            )
         )}

         {/* The card's own width picks the layout, not the viewport. Its room
             is the page's max-w-7xl less px-8, the 280px rail, gap-4 and the
             card's border and px-6: the window's width minus 421px on the real
             page (QA's measurement), 859px from 1280px up. So the columns turn
             on from a 997px layout width (1012px with a classic scrollbar);
             below that the media stacks over the lines. From 36rem the media
             takes the left column and the lines the right, as on the published
             guide (StepMediaSelector's @min-[36rem] classes switch with it). */}
         <div className="@container">
            <div className="flex flex-col gap-3 @min-[36rem]:grid @min-[36rem]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] @min-[36rem]:items-start @min-[36rem]:gap-x-6">
               <div className="flex min-w-0 flex-col gap-3">
                  {/* On a video guide the step's visual is its video segment
                      below, so no media picking is offered — the selector
                      appears only for media already on the step (e.g. images
                      from before the video), so it can still be seen and
                      removed. */}
                  {(!video || stepHasMedia) && (
                     <StepMediaSelector
                        media={media}
                        canAdd={!video}
                        stepBullets={() =>
                           lines.map(line => ({
                              bullet: line.bullet,
                              text: lineDisplayText(line),
                           }))
                        }
                        onChange={setMedia}
                     />
                  )}

                  {video && (
                     <StepTimestampEditor
                        video={video}
                        fields={timestampFields}
                        invalid={timestampsInvalid}
                        onChange={setTimestampFields}
                     />
                  )}
               </div>

               <div className="flex min-w-0 flex-col gap-3">
                  {conflict && (
                     <Alert variant="warning">
                        <AlertTitle>
                           {_js('Someone else saved this step while you were editing.')}
                        </AlertTitle>
                        <AlertDescription>
                           <p>
                              {_js(
                                 'Saving again overwrites their version, and deleting removes it. You can also load their version and redo your changes on top of it.'
                              )}
                           </p>
                           <Button
                              type="button"
                              className={cn('button-md button-outline', 'mt-2')}
                              onClick={() => reseedFromStep(conflict)}
                           >
                              {_js('Load their version')}
                           </Button>
                        </AlertDescription>
                     </Alert>
                  )}

                  <span aria-live="polite" className="sr-only">
                     {reorderAnnouncement}
                  </span>

                  {/* pl-0/list-none: no Tailwind preflight on PHP pages, so
                      lists keep the browser's 40px padding-inline-start unless
                      reset. gap-2.5 on 32px rows spaces single-line lines 42px
                      apart, like the guide page (22px line + 20px margin). */}
                  <ol className="m-0 flex list-none flex-col gap-2.5 p-0 [--line-indent:1rem] md:[--line-indent:1.75rem]">
                     {/* Rows render from the drag preview so a drag shows the
                         tree as the drop would leave it. The action closures
                         still edit `lines`; they can't fire mid-drag (the
                         pointer is captured), and outside a drag the two lists
                         are the same. */}
                     {viewLines.map((line, index) => (
                        <LineRow
                           key={line.key}
                           index={index}
                           line={line}
                           isEditing={editing?.key === line.key}
                           draft={editing?.key === line.key ? editing.text : ''}
                           isDragSource={drag.draggingKeys.has(line.key)}
                           dragRowProps={drag.rowProps}
                           dragHandleProps={drag.handleProps}
                           onBeginEdit={beginEditingAt}
                           onDraftChange={onLineDraftChange}
                           onFinishEdit={finishEditingFor}
                           onShortcutKey={handleLineShortcut}
                           onChangeBullet={updateLine}
                           canDelete={canDelete(viewLines, index)}
                           canInsert={canAddLine(viewLines)}
                           onDelete={deleteLineAt}
                           onInsertBelow={insertAndEdit}
                        />
                     ))}
                  </ol>

                  {/* Always visible (the in-toolbar + only exists while a line
                      is being edited): the discoverable way to append a line. */}
                  <button
                     type="button"
                     disabled={!canAddLine(viewLines)}
                     onClick={() => insertAndEdit(viewLines.length - 1, 'below')}
                     className="flex h-9 w-fit items-center gap-2 rounded-lg px-2 text-sm text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
                  >
                     <FaIcon name="plus" className="size-3.5" aria-hidden />
                     {_js('Add a line')}
                  </button>

                  {problems.length > 0 && (
                     <ul className="m-0 list-none p-0 text-sm text-red-600">
                        {problems.map(problem => (
                           <li key={`${problem.code}-${'line' in problem ? problem.line : ''}`}>
                              {problemMessage(problem)}
                           </li>
                        ))}
                     </ul>
                  )}
               </div>
            </div>
         </div>

         {saveError != null && !isConflictError && (
            <Alert variant="critical">
               <AlertTitle>{_js('The step was not saved.')}</AlertTitle>
               <AlertDescription>
                  {apiErrorMessage(saveError, { login: _js('Log in to edit this guide.') })}
               </AlertDescription>
            </Alert>
         )}

         <div
            // mt-auto drops the bar to the bottom of this editor's h-full
            // column; mb-0 drops the bleed the Steps card zeroes (md:!pb-0).
            className={cn(BAR_CLASSES, 'mb-0 mt-auto flex items-center justify-between gap-2')}
            // Swallow mousedown so clicking Save mid-edit doesn't blur the
            // line editor first: the blur collapses the editor, and the
            // buttons move out from under the in-flight click. The draft is
            // already in the payload, so nothing needs the blur commit.
            onMouseDown={event => event.preventDefault()}
         >
            {step === null ? (
               <span />
            ) : (
               <DangerConfirmButton
                  label={_js('Delete step')}
                  pendingLabel={_js('Deleting...')}
                  title={_js('Delete this step?')}
                  description={_js(
                     'The step and its lines are removed from the guide when you confirm.'
                  )}
                  isPending={deleteStep.isPending}
                  disabled={saveStep.isPending}
                  onConfirm={handleDelete}
               />
            )}
            <div className="ml-auto flex items-center gap-2">
               {onSelectStep && previousStep && (
                  <StepPagerButton
                     direction="previous"
                     neighbor={previousStep}
                     onSelect={onSelectStep}
                  />
               )}
               {onSelectStep && nextStep && (
                  <StepPagerButton direction="next" neighbor={nextStep} onSelect={onSelectStep} />
               )}
               <PrimaryButton
                  isPending={saveStep.isPending}
                  disabled={saveDisabled}
                  onClick={() => handleSave()}
               >
                  {saveStep.isPending
                     ? _js('Saving...')
                     : conflict
                       ? _js('Save and overwrite')
                       : step === null
                         ? _js('Add step')
                         : _js('Save step')}
               </PrimaryButton>
            </div>
         </div>
      </div>
   );
}

/**
 * A quiet "‹ Step 6" / "Step 8 ›" button for the footer, so finishing a step
 * doesn't mean scrolling back up to the header arrows. The neighbor's title,
 * when it has one, is the tooltip. Hidden below lg: the pane header's arrows
 * cover md and up and the step picker's cover phones, and in a narrower card
 * the pair doesn't fit beside Delete and Save (at a 768px window it wrapped
 * the bar to a second row). data-step-pager lets StepsTab find the button to
 * focus after a click, since the editor (and this button with it) remounts
 * per step.
 */
function StepPagerButton({
   direction,
   neighbor,
   onSelect,
}: {
   direction: 'previous' | 'next';
   neighbor: NeighborStep;
   onSelect: (stepid: number, direction: 'previous' | 'next') => void;
}) {
   return (
      <button
         type="button"
         data-step-pager={direction}
         title={neighbor.title === '' ? undefined : neighbor.title}
         onClick={() => onSelect(neighbor.stepid, direction)}
         // Not button-md: its has-[>svg] padding would beat px-2 here.
         className="button-ghost h-9 px-2 text-gray-600 hover:text-gray-950 max-lg:hidden"
      >
         {direction === 'previous' && <FaIcon name="chevron-left" aria-hidden />}
         <span>{_js('Step %1', String(neighbor.number))}</span>
         {direction === 'next' && <FaIcon name="chevron-right" aria-hidden />}
      </button>
   );
}

/** The wikitext-visible character count beside the line toolbar. */
function LineCounter({ text }: { text: string }) {
   return <LineCount length={visibleLength(text)} max={MAX_LINE_CHARS} />;
}

/**
 * One line's row. Memoized: the parent re-renders on every keystroke of the
 * line being edited (and on state that has nothing to do with any line, like
 * saveError), and without this a step with a full line list re-renders and
 * re-diffs every other row on each of those. The props below are the stable
 * half of that contract — see the callers in StepEditor for how each is kept
 * referentially stable (useCallback, or a plain index/key primitive that
 * this component itself closes over instead of the parent).
 */
const LineRow = memo(function LineRow({
   index,
   line,
   isEditing,
   draft,
   isDragSource,
   dragRowProps,
   dragHandleProps,
   onBeginEdit,
   onDraftChange,
   onFinishEdit,
   onShortcutKey,
   onChangeBullet,
   canDelete,
   canInsert,
   onDelete,
   onInsertBelow,
}: {
   index: number;
   line: EditorLine;
   isEditing: boolean;
   /** The in-progress text while this row is editing; parent-owned. */
   draft: string;
   /** Part of the subtree being dragged, shown lifted. */
   isDragSource: boolean;
   dragRowProps: (key: string) => LineDragRowProps;
   dragHandleProps: (key: string) => LineDragHandleProps;
   onBeginEdit: (index: number) => void;
   onDraftChange: (text: string) => void;
   onFinishEdit: (key: string) => void;
   /** Line shortcuts (Ctrl+N/P/D/…); true when the key was consumed. */
   onShortcutKey: (index: number, event: LineShortcutEvent) => boolean;
   onChangeBullet: (key: string, update: Partial<EditorLine>) => void;
   canDelete: boolean;
   canInsert: boolean;
   onDelete: (index: number) => void;
   onInsertBelow: (index: number, where: 'above' | 'below') => void;
}) {
   const rowProps = dragRowProps(line.key);
   const handleProps = dragHandleProps(line.key);
   return (
      <li
         {...rowProps}
         className={cn(
            // pl-2 pairs with the -0.5rem in the margin below: the hover
            // and drag boxes reach left past the grip (which otherwise
            // hangs off their edge) while the content keeps its exact x.
            'group/line flex items-start gap-1 rounded-md py-0.5 pl-2 hover:bg-gray-50',
            // The whole row is the drag surface, so it wears the grab
            // cursor; the text keeps its own pointer to read as clickable.
            // While the line is being edited the row reads as a text
            // field, so the grab cursor retreats to the grip.
            !isEditing && 'cursor-grab active:cursor-grabbing',
            isDragSource && 'opacity-60 ring-2 ring-brand-300'
         )}
         // Merged by hand: a bare style prop would silently replace the
         // row props' touch-action, re-breaking touch scrolling.
         style={{
            ...rowProps.style,
            marginLeft: `calc(${line.level} * var(--line-indent) - 0.5rem)`,
         }}
      >
         {/* Drag to reorder; sideways to indent. The Ctrl shortcuts
             (arrows, brackets) stay the keyboard path, so the grip is
             pointer-only. A vertical touch drag can only begin on this
             grip (the row keeps pan-y so it still scrolls), so the
             before:-inset pseudo widens the touch target well past the
             16px icon column without growing the box — the bullet's
             alignment and the row height stay put. */}
         <span
            {...handleProps}
            aria-hidden
            // h-7 matches the bullet trigger (size-7) so the two icons
            // share a horizontal center; the row top-aligns its children.
            // Hidden at rest so the line reads as on the guide page; it
            // still takes its space (and the touch target stays put), and
            // touch screens keep it showing since a vertical drag can only
            // begin here.
            className="relative -ml-1 flex h-7 w-4 shrink-0 cursor-grab items-center justify-center text-gray-300 opacity-0 transition-opacity hover:text-gray-500 group-focus-within/line:opacity-100 group-hover/line:opacity-100 active:cursor-grabbing pointer-coarse:opacity-100 before:absolute before:-inset-y-1 before:-left-4 before:right-0 before:content-['']"
         >
            <FaIcon name="grip-vertical" className="size-3" />
         </span>
         {/* ml-1.5 centers the bullet mark between its neighbors' visible
             edges: grip icon inset (2px) + gap (4px) + 6px + the trigger's
             own 8px inset on the left equals the 8px inset + gap (4px) +
             the text's px-2 (8px) on the right — 20px each side. */}
         <div className="ml-1.5">
            <BulletSelector
               value={line.bullet}
               onChange={bullet => onChangeBullet(line.key, { bullet })}
            />
         </div>

         {isEditing ? (
            <LineWysiwyg
               initialText={line.text}
               value={draft}
               onChange={onDraftChange}
               onFinish={() => onFinishEdit(line.key)}
               onShortcutKey={event => onShortcutKey(index, event)}
               counter={<LineCounter text={draft} />}
               /* Reordering and indenting are the drag grip's job (or the
                  Ctrl shortcuts); the toolbar keeps only what a drag can't
                  do: adding and deleting lines. */
               toolbarStart={
                  <LineButton
                     label={_js('Add a line below')}
                     icon="plus"
                     disabled={!canInsert}
                     onClick={() => onInsertBelow(index, 'below')}
                  />
               }
               toolbar={
                  <LineButton
                     label={_js('Delete line')}
                     icon="trash-can"
                     disabled={!canDelete}
                     isDestructive
                     onClick={() => onDelete(index)}
                  />
               }
            />
         ) : (
            <button
               type="button"
               onClick={() => onBeginEdit(index)}
               // Drags may start on the read-mode text (the row is the drag
               // surface); a motionless tap still lands here as a click.
               data-line-drag-through
               // The guide page's line: 16px on 22px, red for a caution.
               // py-0.75 makes the one-line button 28px, the bullet
               // trigger's height, so the text and bullet share a center.
               className={cn(
                  'min-w-0 flex-1 cursor-pointer rounded-md px-2 py-0.75 text-left text-base leading-5.5',
                  line.bullet === 'icon_caution' ? 'text-red-600' : 'text-gray-900'
               )}
            >
               {/* 55ch is the guide page's measure; the button stays the
                   full row so the whole row is the click target. */}
               <span className="block max-w-[55ch] text-pretty">
                  {line.text.trim().length === 0 ? (
                     <span className="text-gray-500">{_js('Empty line — click to write')}</span>
                  ) : line.rendered === null ? (
                     <span>{line.text}</span>
                  ) : (
                     <span
                        // Server-rendered wikitext from the GuideStep parser.
                        dangerouslySetInnerHTML={{ __html: line.rendered }}
                     />
                  )}
               </span>
            </button>
         )}
      </li>
   );
});
